import { useRef } from 'react';
import { useStore, useDispatch } from 'react-redux';
import { setListState, clearListState } from '../store/reducers/listStateSlice';

/**
 * Persist a listing page's FILTERS (search / global search / sorting / page size)
 * in redux, keyed by moduleKey, so returning from add/edit/view restores them.
 *
 * The PAGE is NOT stored here — it lives entirely in the route. Every page-aware
 * route carries it (list /list/:id, add /:pgno/add, edit /:pgno/edit/:id,
 * view /:pgno/view/:id), so components read the page from their own params and
 * navigate back to /list/{that page}.
 *
 * Usage in a list:
 *   const { saved, save, clear } = usePersistedListState('brands');
 *   const [page, setPage]     = useState(Number(params?.id) || 1);   // page from route
 *   const [search, setSearch] = useState(saved?.search || {});        // filters from store
 *   useEffect(() => { save({ itemPerPage, sorting, search, globalSearch }); },
 *            [itemPerPage, sorting, search, globalSearch]);
 *   // Reset Filter: clear();
 *
 * In-memory per browser tab (no cross-tab sharing) — lost on hard refresh.
 */
export const usePersistedListState = (moduleKey) => {
    const store = useStore();
    const dispatch = useDispatch();

    // read once (no subscription → no re-render churn as the list saves its own state)
    const savedRef = useRef(store.getState().listState[moduleKey]);
    const saved = savedRef.current;

    const save = (value) => dispatch(setListState({ key: moduleKey, value }));
    const clear = () => dispatch(clearListState(moduleKey));

    return { saved, save, clear };
};

export default usePersistedListState;
